"use client";

import React, {useState, useEffect, useRef} from "react";

import Image from "next/image";
import CornerChip from "@/components/global/ui/CornerChip";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

import grid from "@/public/home-new/scroll-grid.svg";
import fireFallbackDesktop from "@/public/home-new/fire-fallback-desktop.png";
import fireFallbackMobile from "@/public/home-new/fire-fallback-mobile.png";

export default function ProblemsSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement | null>(null);
  const backgroundVideoRef = useRef<HTMLDivElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const itemPositions = [
    "lg:absolute lg:top-[18.78%] lg:left-[16.5rem]",
    "lg:absolute lg:top-[33.37%] lg:right-[14.375rem]",
    "lg:absolute lg:top-[66%] lg:left-[25.125rem]",
    "lg:absolute lg:top-[64.18%] lg:right-[16.875rem]",
  ];

  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!sectionRef.current || !isLargeScreen || !itemRefs.current.length) return;

    const section = sectionRef.current;
    const items = itemRefs.current.filter(item => item !== null);
    const backgroundVideo = backgroundVideoRef.current;
    if (!items.length) return;

    gsap.set(items, { opacity: 0, y: 50 });
    if (backgroundVideo) {
      gsap.set(backgroundVideo, { opacity: 1 });
    }

    const itemCount = items.length;
    const scrollDistance = 60;
    const initialDelay = 10; // small delay before first item appears

    items.forEach((item, index) => {
      const startProgress = initialDelay + (index / itemCount) * scrollDistance;

      ScrollTrigger.create({
        trigger: section,
        start: `top+=${startProgress}% top`,
        onEnter: () => {
          gsap.to(item, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" });
          if (index === 0 && backgroundVideo) {
            gsap.to(backgroundVideo, { opacity: 0.5, duration: 0.8, ease: "power2.out" });
          }
        },
        onLeaveBack: () => {
          gsap.to(item, { opacity: 0, y: 50, duration: 0.5, ease: "power2.in" });
          if (index === 0 && backgroundVideo) {
            gsap.to(backgroundVideo, { opacity: 1, duration: 0.5, ease: "power2.in" });
          }
        },
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [isLargeScreen, data?.problemsItems]);

  return (
    <section
      ref={sectionRef}
      className={cn(
        "lg:h-[300vh]",
        className
      )}
    >
      <div className="lg:sticky lg:top-0">
        <div className="relative overflow-clip bg-[#0A1A1D] lg:h-screen pt-[11rem] pb-[13rem] flex flex-col justify-center">
          <Image
            className="!w-[116.5rem] lg:!w-[max(100%,243.25rem)] absolute left-1/2 -translate-x-1/2 top-0 !h-full object-cover pointer-events-none"
            src={grid}
            alt="background grid"
          />
          <div className="absolute aspect-[885/1767] left-1/2 -translate-x-1/2 bottom-0 w-full mix-blend-screen opacity-50 md:hidden pointer-events-none">
            <BackgroundVideo
              playbackId={"G00nG800rltuKOwiy7eH006FR7Us889PYecx4S1VQj1O500"}
              fallbackImage={fireFallbackMobile}
            />
          </div>
          <div
            ref={backgroundVideoRef}
            className="absolute left-0 bottom-0 w-full h-full mix-blend-screen hidden md:block pointer-events-none"
            style={{ opacity: 1 }}
          >
            <BackgroundVideo
              playbackId={"4YcQver7zBeOPhRwRJnkpKrAj8eRC1yqdj1NBPbCOZc"}
              fallbackImage={fireFallbackDesktop}
            />
          </div>

          <div className="container flex flex-col items-center">
            <Chip
              image={data?.problemsChip?.icon}
              text={data?.problemsChip?.text}
              imageBG="bg-[#F97474]"
            />

            {data?.problemsTitle && (
              <div className="richtext prose-h2:text-white w-[min(71.25rem,100%)] lg:prose-headings:text-[8rem] mb-[9rem] lg:mb-0">
                <PortableText
                  value={data.problemsTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.problemsItems && (
              <div className="w-full flex flex-col gap-[8rem]">
                {data?.problemsItems.map((item, index) => (
                  <div
                    key={index}
                    ref={el => {
                      (itemRefs.current[index] = el);
                    }}
                    className={cn(
                      "lg:self-auto border-t-[.25rem] lg:border-t-[.75rem] border-t-[#F97474] lg:mb-0",
                      index % 2 === 0 ? "self-start" : "self-end",
                      item.image ? "w-[40rem] lg:w-[56rem]" : "w-[37rem] lg:w-[48.5rem]",
                      itemPositions[index]
                    )}
                  >
                    <div className={cn(
                      "relative bg-white  flex items-center gap-[2rem] lg:gap-[3rem]",
                      item.image ? "p-[.5rem]" : "p-[2rem] lg:p-[3rem]"
                    )}>
                      <CornerChip
                        className="absolute bottom-full -left-px prose-p:uppercase prose-img:!w-[2rem]"
                        image={item?.chip?.icon}
                        text={item?.chip?.text}
                        style={{ backgroundColor: '#F97474' }}
                      />

                      {item?.image ? (
                        <Image
                          className="!w-full"
                          src={item.image}
                          alt=""
                        />
                      ) : (
                        <>
                          {item?.icon && (
                            <Image
                              className="!w-[5rem] lg:!w-[6.5rem] flex-shrink-0"
                              src={item.icon}
                              alt=""
                            />
                          )}

                          {item?.text && (
                            <div className="richtext richtext-start prose-p:text-black prose-p:leading-[1.2] prose-p:text-[2.25rem] lg:prose-p:text-[2.875rem]">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
